import { URL_BACKEND } from "../lib/constants/urls/urlBackend";
import { consoleErrorPackagesSdk } from "../utils/consoleErrorPackagesSdk";
import { RequestError } from "./errors/requestError.error";
import type { paths } from "./generated/schema";
import type { RequestType } from "./types/enums/requestType.enum";
import type { RequestPropsNext } from "./types/requestPropsNext.types";
import type { GetRequestHeadersProps } from "./utils/getRequestHeaders";
import { getRequestHeaders } from "./utils/getRequestHeaders";

// Type helpers
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

type PathsWithMethod<M extends HttpMethod> = {
  [P in keyof paths]: paths[P] extends { [K in Lowercase<M>]: unknown }
    ? P
    : never;
}[keyof paths];

type RequestBody<
  P extends keyof paths,
  M extends HttpMethod,
> = paths[P] extends {
  [K in Lowercase<M>]: {
    requestBody: { content: { "application/json": infer B } };
  };
}
  ? B
  : undefined;

type RequestParams<
  P extends keyof paths,
  M extends HttpMethod,
> = paths[P] extends { [K in Lowercase<M>]: { parameters: infer Params } }
  ? Params
  : undefined;

type ResponseType<
  P extends keyof paths,
  M extends HttpMethod,
> = paths[P] extends {
  [K in Lowercase<M>]: {
    responses: { 200: { content: { "application/json": infer R } } };
  };
}
  ? R
  : void;

// Build URL with path parameters
function buildUrl<P extends keyof paths>(
  path: P,
  params?: { path?: Record<string, string | number> },
): string {
  let url = String(path);
  if (params?.path) {
    Object.entries(params.path).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, String(value));
    });
  }
  return `${URL_BACKEND}${url}`;
}

// Request options
export type OpenApiRequestOptions<
  P extends keyof paths,
  M extends HttpMethod,
> = RequestPropsNext & {
  urlBackendPath: P;
  method: M;
  params?: RequestParams<P, M>;
  body?: RequestBody<P, M>;
  requestType?: RequestType;
  headersValues?: GetRequestHeadersProps["values"];
};

/**
 * Type-safe server-side request using OpenAPI types.
 *
 * @example
 * // GET request
 * const data = await openApiRequest({
 *   method: "get",
 *   path: "/sessions/count",
 *   requestType: "trusted",
 *   next: { tags: ["sessions-count"], revalidate: 3600 },
 * });
 *
 * @example
 * // POST request with body
 * const data = await openApiRequest({
 *   method: "post",
 *   path: "/family/users",
 *   body: { email: "test@example.com" },
 * });
 */
export async function openApiRequest<
  M extends HttpMethod,
  P extends PathsWithMethod<M>,
>({
  urlBackendPath,
  method,
  params,
  body,
  requestType = "authenticated",
  headersValues,
  ...nextOptions
}: OpenApiRequestOptions<P, M>): Promise<ResponseType<P, M>> {
  const url = buildUrl(
    urlBackendPath,
    params as { path?: Record<string, string | number> },
  );

  const headers = await getRequestHeaders({
    requestType,
    values: headersValues,
  });

  const response = await fetch(url, {
    method: method,
    credentials: requestType === "other" ? "omit" : "include",
    headers: {
      ...headers,
      ...(body && { "Content-Type": "application/json" }),
    },
    ...(body && { body: JSON.stringify(body) }),
    ...nextOptions,
  });

  if (!response.ok) {
    const errorJson = await response.json().catch(() => null);
    const errorMessage =
      errorJson?.error?.message ?? errorJson?.error ?? response.statusText;

    consoleErrorPackagesSdk({
      error: errorMessage,
      heading: `Failed request ${method} ${url}`,
    });

    throw new RequestError(errorMessage, response.status, errorJson?.code);
  }

  if (response.status === 204) {
    return undefined as ResponseType<P, M>;
  }

  return response.json() as Promise<ResponseType<P, M>>;
}
